// The single Agent page route container: the agent detail query,
// rename/archive mutations, and the `?connection`/`?version`/`?new`
// search-param ↔ drawer-subject mapping (URL is UI state, §4c). Personas live on the
// top-level /personas route; this page is the agent identity + its
// immutable version history + interfaces. Sections live in `./agent/`; each
// owns its list/detail queries and mutations. There is deliberately no
// built-in per-agent metrics/dashboard section: it is too early to know what
// belongs on one, and custom analytics live on /dashboard. Review is the
// agent-scoped QA entry point.
import type { ReactNode } from "react";
import { useCallback, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import type { components } from "../api/schema";
import {
  useWorkingScope,
  withWorkingScopeSearch,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { isShowcaseMode } from "../lib/showcaseMode";
import { parseAgentSearch, type AgentSearch } from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import {
  EmptyState,
  InlineError,
  Link,
  Page,
  PageHeader,
  Section,
  Skeleton,
  Stack,
} from "../ui";
import { AgentConnections } from "./agent/AgentConnections";
import { AgentVersions } from "./agent/AgentVersions";
import {
  AgentHeaderActions,
  AgentIdentityDetails,
} from "./agent/AgentOverview";
import { useAgentActions } from "./agent/useAgentActions";
import { ShowcaseReadOnlyNotice } from "./ShowcaseReadOnlyNotice";
type AgentResponse = components["schemas"]["AgentResponse"];

// This route's validated params plus the root-owned `?agent` scope.
type AgentSearchState = AgentSearch & WorkingScopeSearch;

export function Agent() {
  const params = useParams({ strict: false });
  const navigate = useTypedNavigate<AgentSearchState, "/agent">();
  const scope = useWorkingScope();
  // `/agents/$agentId` deep links win over the selector scope (docs/09 §4).
  const agentId =
    (typeof params.agentId === "string" && params.agentId.length > 0
      ? params.agentId
      : undefined) ?? scope.agentId;
  const search = useTypedSearch(withWorkingScopeSearch(parseAgentSearch));
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showRenameForm, setShowRenameForm] = useState(false);

  // Functional-merge navigation ONLY: patch this route's own params over the
  // previous search so the root-owned `?agent` scope always survives.
  const patchSearch = useCallback(
    (patch: Partial<AgentSearch>, replace: boolean) => {
      void navigate({
        replace,
        search: mergeSearch<AgentSearchState>(patch),
      });
    },
    [navigate],
  );
  // One drawer at a time (§4a): every open clears the other subjects;
  // closes are replaces so Back never reopens a dismissed drawer.
  const setEditorSearch = useCallback(
    (patch: Partial<AgentSearch>, replace: boolean) => {
      patchSearch(
        { connection: undefined, new: undefined, version: undefined, ...patch },
        replace,
      );
    },
    [patchSearch],
  );
  const closeEditors = useCallback(() => {
    setEditorSearch({}, true);
  }, [setEditorSearch]);

  const agent = useQuery({
    enabled: Boolean(agentId),
    queryKey: queryKeys.agents.detail(agentId ?? "pending"),
    queryFn: async () => {
      if (!agentId) {
        throw new Error("Agent id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/agents/{id}", {
        params: { path: { id: agentId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  // Archive success clears the scope and lands on the bare /agent page.
  const { archiveAgent, renameAgent } = useAgentActions({
    agentId,
    onArchived: () => {
      const archivedTeamId = agent.data?.team_id;
      scope.clearAgent(archivedTeamId);
      void navigate({
        search: archivedTeamId === undefined ? {} : { team: archivedTeamId },
        to: "/agent",
      });
    },
    onCloseArchiveConfirm: () => {
      setShowArchiveConfirm(false);
    },
    onCloseRenameForm: () => {
      setShowRenameForm(false);
    },
  });

  // Sections (and their drawers) render as soon as the page has an agent id
  // and the agent read did not fail (agent.md States).
  const sectionsVisible = agentId !== undefined && !agent.isError;

  return (
    <AgentView
      agent={agent.data ?? null}
      archiveError={
        archiveAgent.isError
          ? requestErrorDescription(archiveAgent.error)
          : null
      }
      connectionsSection={
        sectionsVisible ? (
          <AgentConnections
            agentId={agentId}
            editingConnectionId={search.connection ?? null}
            isCreateOpen={search.new === "connection"}
            onCloseEditor={closeEditors}
            onOpenCreate={() => {
              setEditorSearch({ new: "connection" }, false);
            }}
            onOpenEdit={(connectionId) => {
              setEditorSearch({ connection: connectionId }, false);
            }}
          />
        ) : null
      }
      versionsSection={
        sectionsVisible ? (
          <AgentVersions
            agentId={agentId}
            selectedVersionId={search.version ?? null}
            onCloseDetail={closeEditors}
            onOpenDetail={(versionId) => {
              setEditorSearch({ version: versionId }, false);
            }}
          />
        ) : null
      }
      isArchivePending={archiveAgent.isPending}
      isLoadingAgent={agent.isPending && Boolean(agentId)}
      isRenamePending={renameAgent.isPending}
      loadError={agent.isError ? agent.error.message : null}
      onArchive={() => {
        archiveAgent.mutate();
      }}
      onCancelArchive={() => {
        setShowArchiveConfirm(false);
      }}
      onCancelRename={() => {
        setShowRenameForm(false);
      }}
      onRename={async (displayName) => {
        await renameAgent.mutateAsync(displayName);
      }}
      onStartArchive={() => {
        setShowArchiveConfirm(true);
        setShowRenameForm(false);
      }}
      onStartRename={() => {
        setShowRenameForm(true);
        setShowArchiveConfirm(false);
      }}
      showArchiveConfirm={showArchiveConfirm}
      showRenameForm={showRenameForm}
    />
  );
}

export interface AgentViewProps {
  agent: AgentResponse | null;
  archiveError: string | null;
  connectionsSection: ReactNode;
  versionsSection?: ReactNode;
  isArchivePending: boolean;
  isLoadingAgent: boolean;
  isRenamePending: boolean;
  loadError: string | null;
  onArchive: () => void;
  onCancelArchive: () => void;
  onCancelRename: () => void;
  onRename: (displayName: string) => Promise<void>;
  onStartArchive: () => void;
  onStartRename: () => void;
  showArchiveConfirm: boolean;
  showRenameForm: boolean;
}

export function AgentView({
  agent,
  archiveError,
  connectionsSection,
  versionsSection,
  isArchivePending,
  isLoadingAgent,
  isRenamePending,
  loadError,
  onArchive,
  onCancelArchive,
  onCancelRename,
  onRename,
  onStartArchive,
  onStartRename,
  showArchiveConfirm,
  showRenameForm,
}: AgentViewProps) {
  return (
    <Page variant="config">
      <Stack gap={6}>
        <PageHeader
          className="marker-agent-header"
          description={agent ? <AgentIdentityDetails agent={agent} /> : null}
          title={agent?.display_name}
          actions={
            agent ? (
              isShowcaseMode ? (
                <ShowcaseReadOnlyNotice />
              ) : (
                <AgentHeaderActions
                  agent={agent}
                  archiveError={archiveError}
                  isArchivePending={isArchivePending}
                  isRenamePending={isRenamePending}
                  onArchive={onArchive}
                  onCancelArchive={onCancelArchive}
                  onCancelRename={onCancelRename}
                  onRename={onRename}
                  onStartArchive={onStartArchive}
                  onStartRename={onStartRename}
                  showArchiveConfirm={showArchiveConfirm}
                  showRenameForm={showRenameForm}
                />
              )
            ) : null
          }
        />

        {!agent && !isLoadingAgent && !loadError ? (
          <Section>
            <EmptyState title="No agent selected" />
          </Section>
        ) : null}

        {isLoadingAgent ? (
          <Skeleton label="Loading agent" rows={5} shape="detail-field" />
        ) : null}

        {loadError ? <InlineError>{loadError}</InlineError> : null}

        {agent && !isShowcaseMode ? (
          <Link href={`/review?agent=${encodeURIComponent(agent.id)}`}>
            Review markers
          </Link>
        ) : null}

        {connectionsSection}

        {versionsSection}
      </Stack>
    </Page>
  );
}
